import { dateRangeFilterString } from '@/helpers/dateHelper';
import { TableFiltersProps } from '@/types/table';
import useFilterFields from '@/utils/hooks/useFilterFields';
import useLanguage from '@/utils/hooks/useLanguage';
import { DepositWithdrawalRequestFilters, RequestStatusFilter } from '@/XplorPortal/PortalDeposit';
import { useDateFormatter } from '@react-aria/i18n';
import { Updater } from '@tanstack/react-table';
import cn from 'classnames';
import { useEffect, useMemo } from 'react';
import AppliedFilters from './AppliedFilters';
import DateRangeFilters from './DateRangeFilters';
import FilterSection from './FilterSection';
import GeneralSearchFilter from './GeneralSearchFilter';
import RadioButtonFilter from './RadioButtonFilter';

const requestStatusFilters: { label: string; value: 'All' | RequestStatusFilter }[] = [
    { label: 'All', value: 'All' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Processing', value: 'Processing' },
    { label: 'Canceled', value: 'Canceled' },
    { label: 'Rejected', value: 'Rejected' }
];

interface DepositWithdrawalRequestTableFiltersProps extends TableFiltersProps {
    filters: DepositWithdrawalRequestFilters;
    setFilters: (filters: DepositWithdrawalRequestFilters) => void;
    canFilterDateRange?: boolean;
    defaultFilters: any;
    resetPageNumber: (updater: Updater<number>) => void;
}

export const DepositWithdrawalRequestTableFilters = (props: DepositWithdrawalRequestTableFiltersProps) => {
    const { opened, filters, setFilters, defaultFilters, globalFilter, setGlobalFilter, resetPageNumber } = props;

    const { startDate, endDate } = filters;
    const { setFilterFields } = useFilterFields(filters, setFilters);

    const lang = useLanguage();
    const dateFormatter = useDateFormatter({ timeZone: 'UTC' });

    useEffect(() => resetPageNumber(0), [globalFilter, filters, dateFormatter]);

    const appliedFilters = useMemo(() => {
        return {
            generalSearch: {
                value: globalFilter,
                label: lang.blotters.generalSearch
            },
            requestStatus: filters.requestStatus,
            dateRange: {
                value: dateRangeFilterString(startDate.value, endDate.value, dateFormatter),
                label: lang.common.dateRange
            }
        };
    }, [lang, globalFilter, filters, dateFormatter]);

    return (
        <div className="flex flex-col gap-3 text-neutral-200">
            {opened && (
                <div className={cn('flex flex-col w-full bg-background-dark border border-neutral-700 rounded-md')}>
                    <FilterSection first>
                        <GeneralSearchFilter
                            globalFilter={globalFilter}
                            setGlobalFilter={setGlobalFilter}
                            onReset={() => {
                                setGlobalFilter('');
                                setFilters(defaultFilters);
                            }}
                        />
                    </FilterSection>
                    <FilterSection>
                        <RadioButtonFilter
                            name="requestStatus"
                            label={filters['requestStatus'].label}
                            appliedFilters={appliedFilters}
                            radioButtonFilters={requestStatusFilters}
                            setFilterFields={setFilterFields}
                        />
                    </FilterSection>
                    <FilterSection>
                        <DateRangeFilters
                            startDate={startDate}
                            endDate={endDate}
                            setFilterFields={setFilterFields}
                            allowYearRange
                        />
                    </FilterSection>
                </div>
            )}
            <AppliedFilters appliedFilters={appliedFilters} />
        </div>
    );
};
